import { ChipCellRenderer } from "@/controls/ag-grid/components/ChipCellRenderer";
import {
	getMomentTz,
	useShiftNavigator,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Tooltip } from "@mui/material";
import { ColDef, ValueGetterParams } from "ag-grid-enterprise";
import { get } from "lodash-es";
import React from "react";
import {
	LinkCellRenderer,
	ManualIconCellRenderer,
} from "../../../../../controls/ag-grid/components/ButtonCellRenderer";
const moment = getMomentTz();
type Props = {};

export const useTableData = ({}: Props) => {
	const { shiftInfo } = useShiftNavigator();
	const { state } = useUTLSettingsContext();

	const MAX_TTT = get(state?.settings, "maxTTT", 180);

	const shiftId = get(shiftInfo, "CurrentShiftId", null);

	const columns: ColDef[] = [
		{
			field: "AssetName",
			headerName: "EAF",
			cellRenderer: ChipCellRenderer,
			cellClass: (params) =>
				params.data.AssetId ? `eaf-${params.data.AssetId}` : "",
			minWidth: 88,
			flex: 1,
		},
		{
			field: "HeatName",
			headerName: "Heat",

			cellRenderer: (params: any) => {
				return LinkCellRenderer({
					...params,
					link: `/meltshop-heats/${shiftId}?heatId=${params.data.HeatId}`,
				});
			},
			minWidth: 65,
			flex: 1,
		},
		{
			field: "GradeName",
			headerName: "Grade",
			minWidth: 80,
			flex: 1,
		},
		{
			field: "LadleName",
			headerName: "Ladle",
			minWidth: 60,
			flex: 1,
		},
		{
			field: "Start",
			headerName: "Start",
			cellRenderer: (params: any) => {
				return LinkCellRenderer({
					...params,
					link: `/eaf-data-entry/${params.data.ProcessId}`,
				});
			},
			minWidth: 62,
			valueGetter: (params) =>
				params.data.Start ? moment(params.data.Start).format("HH:mm") : "",
			flex: 1,
		},
		{
			field: "End",
			headerName: "End",
			valueGetter: (params) =>
				params.data.End ? moment(params.data.End).format("HH:mm") : "",
			minWidth: 62,

			flex: 1,
		},
		{
			field: "TapToTap",
			headerName: "Tap To Tap",
			minWidth: 90,
			cellRenderer: (params: any) => {
				const value = params.value;
				const isOverLimit = Number(value) > MAX_TTT;

				const spanContent = (
					<span
						style={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							textAlign: "right",
						}}
					>
						{value}
					</span>
				);
				if (isOverLimit) {
					return (
						<Tooltip
							title={`Maximum limit: ${MAX_TTT} minutes`}
							placement="top"
						>
							{spanContent}
						</Tooltip>
					);
				}

				return spanContent;
			},
			cellClassRules: {
				"max-limit": (params) => {
					return Number(params.value) > MAX_TTT; //3hs in minutes
				},
			},
			flex: 1,
		},
		{
			field: "PowerOn",
			headerName: "Power On",
			type: ["numericColumn", "numericWidth"],
			minWidth: 84,
			flex: 1,
		},
		{
			field: "MWH",
			headerName: "MWH",
			valueGetter: (params: ValueGetterParams) => {
				return params.data.MWH ? params.data.MWH.toFixed(1) : "";
			},
			minWidth: 55,
			cellStyle: {
				textAlign: "right",
			},
			flex: 1,
		},
		{
			field: "OxygenVolume",
			headerName: "O2 Vol [scf]",
			valueGetter: (params: ValueGetterParams) => {
				return params.data.OxygenVolume
					? params.data.OxygenVolume.toFixed(1)
					: "";
			},
			cellStyle: {
				textAlign: "right",
			},
			minWidth: 100,
			flex: 1,
		},
		{
			field: "Charges",
			headerName: "Charges",
			type: ["numericColumn", "numericWidth"],
			minWidth: 76,

			flex: 1,
		},
		{
			field: "ScrapTons",
			headerName: "Scrap [lbs]",
			type: ["numericColumn", "numericWidth"],
			minWidth: 92,
			flex: 1,
		},
		{
			field: "LastTemperature",
			headerName: "Last Temp",
			type: ["numericColumn", "numericWidth"],
			minWidth: 92,
			cellStyle: {
				textAlign: "right",
			},
			flex: 1,
		},
		{
			field: "TrimProbeTemperature",
			headerName: "Trim Temp",
			minWidth: 89,
			cellStyle: {
				textAlign: "right",
			},
			flex: 1,
		},
		{
			field: "DeltaT",
			headerName: "Delta T",
			minWidth: 68,
			cellStyle: {
				textAlign: "right",
			},
			flex: 1,
		},
		{
			field: "SteelWeight",
			headerName: "Steel Weight",
			minWidth: 102,
			cellStyle: {
				textAlign: "right",
			},
			flex: 1,
		},
		{
			field: "IsProcessManual",
			headerName: "",
			type: ["nonSorteable", "nonFilterable", "centerAligned"],
			cellRenderer: ManualIconCellRenderer,
			cellClass: "ag-button-cell",
			minWidth: 80,
			flex: 1,
		},
	];

	return { columns };
};
